/**
 * Input validation and sanitization utilities to prevent XSS and injection attacks
 * Uses DOMPurify for robust security against all XSS vectors
 */
import { Rule } from 'antd/es/form';
import DOMPurify from 'dompurify';

/**
 * DOMPurify configuration for strict sanitization
 */
const SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [], // No HTML tags allowed
  ALLOWED_ATTR: [], // No attributes allowed
  KEEP_CONTENT: true, // Keep text content when removing tags
  RETURN_DOM: false, // Return string not DOM
  RETURN_DOM_FRAGMENT: false,
};

/**
 * Sanitizes string by removing HTML tags and dangerous characters using DOMPurify
 *
 * Handles edge cases including:
 * - HTML entities (&lt;script&gt;)
 * - Malformed tags (<sCrIpT>)
 * - Various encodings
 * - Protocol handlers (javascript:)
 * - Event handlers (onerror=)
 *
 * @param input - Raw input string
 * @returns Sanitized string with all HTML removed
 *
 * @example
 * sanitizeInput('<script>alert("XSS")</script>Test')  // Returns: 'Test'
 * sanitizeInput('Hello <b>World</b>')                  // Returns: 'Hello World'
 */
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  return DOMPurify.sanitize(input, SANITIZE_CONFIG).trim();
};

/**
 * Validates if input contains HTML tags or dangerous characters
 * Compares original input with sanitized version to detect XSS attempts
 *
 * @param input - Input string to validate
 * @returns true if input is safe, false if contains dangerous content
 *
 * @example
 * isInputSafe('Hello World')              // Returns: true
 * isInputSafe('<script>alert(1)</script>') // Returns: false
 */
export const isInputSafe = (input: string): boolean => {
  if (!input) return true;

  const sanitized = DOMPurify.sanitize(input, SANITIZE_CONFIG);
  return input.trim() === sanitized.trim();
};

/**
 * Ant Design form validator for campaign names
 *
 * Validation rules:
 * 1. Blocks HTML tags and XSS attempts
 * 2. Only allows alphanumeric, spaces, and safe punctuation: . - _ , ( ) &
 *
 * @param _ - Ant Design rule (unused)
 * @param value - Campaign name value
 * @returns Promise that resolves if valid, rejects with error message if invalid
 *
 * @example
 * <Form.Item rules={[{ validator: validateCampaignName }]}>
 */
export const validateCampaignName = (_: Rule, value: string): Promise<void> => {
  if (!value) return Promise.resolve();

  // Check for XSS/HTML injection
  if (!isInputSafe(value)) {
    return Promise.reject(
      new Error(
        'Campaign name contains invalid characters. HTML tags and scripts are not allowed.'
      )
    );
  }

  // Only allow safe characters
  const allowedPattern = /^[a-zA-Z0-9\s\-_.,()&]+$/;
  if (!allowedPattern.test(value)) {
    return Promise.reject(
      new Error(
        'Campaign name can only contain letters, numbers, spaces, and basic punctuation (.-_,()&)'
      )
    );
  }

  return Promise.resolve();
};
